import React, { useEffect, useCallback, useState } from 'react';
import { TextField, FormHelperText } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import debounce from 'debounce';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Controller, useFormContext } from 'react-hook-form';
import { useId } from '../../hooks';
import useStyles from './styles';
import { QuestionCircleIcon } from '../icon/solid';
import { CustomActionCell } from '../index';
import { Option, SelectProps } from '../field-select/types';

type Props = SelectProps & {
  options: Option[];
  loading?: boolean;
  onSearch?: (search: string) => void;

  dependentDefaultOption?: Option;
};

const FormSimpleAutoComplete = ({
  options = [],
  name,
  label,
  hideErrors = false,
  disabled = false,
  disableClearable = false,
  onSearch,
  loading = false,
  tooltip,
  callback,
  isRequired,
  emptyValue = -1,
  dependentFieldName = '',
  dependentDefaultOption,
  selectFirstOption = false,
  setOption,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { watch } = useFormContext();
  const { formState, setValue, errors } = useFormContext();

  const error = errors[name];

  const fieldValue = watch(name);
  const touched = !!formState.touched[name];
  const autoCompleteId = useId('form-input-');
  const inputId = useId('form-input-');
  const errorId = useId('input-error-');
  const [textValue, setTextValue] = useState<string>('');
  const [textValueBeforeSearch, setTextValueBeforeSearch] = useState<string>('');
  const inputRef: React.MutableRefObject<HTMLInputElement | null | undefined> = React.useRef();
  const [updateFocus, setUpdateFocus] = useState(false);
  const [initOptions, setInitOptions] = useState(false);
  // const [initialOptionsInstalled, setInitialOptionsInstalled] = useState(false);
  const dependentFieldValue = watch(dependentFieldName);
  const [firstDependentValue] = useState(dependentFieldValue);

  const clearStates = useCallback((clearTextField: boolean = true) => {
    setValue(name, emptyValue, { shouldValidate: true, shouldDirty: true });
    if (clearTextField) {
      setTextValue('');
    }

    if (callback) {
      callback();
    }
  }, [setValue, name, setTextValue, emptyValue, callback]); // setFieldTouched

  const callbackOnChangeTextField = useCallback(debounce((search: string) => {
    if (onSearch) {
      onSearch(search);
      setUpdateFocus(true);
    }
  }, 600), [onSearch, setUpdateFocus]);

  const onChangeTextField = useCallback((event) => {
    setTextValue(event.target.value || '');
    callbackOnChangeTextField(event.target.value);
  }, [callbackOnChangeTextField]);

  // On change select
  const onChange = useCallback((event, option: Option) => {
    if (option) {
      // on select
      setValue(name, option.id, { shouldValidate: true, shouldDirty: true });

      setTextValue(option.label);
      if (event.key === 'Enter') {
        event.stopPropagation();
      }
      if (callback) {
        callback(option);
      }
    } else {
      // on clear
      clearStates();
      callbackOnChangeTextField('');
    }
  }, [setValue, name, setTextValue, clearStates, callbackOnChangeTextField, callback]); // setFieldTouched

  const labelRender = (
    <div>
      {label}
      {tooltip && (
        <CustomActionCell
          title={<QuestionCircleIcon className={`${classes.iconTooltip}`} />}
          classNameButtonWrapper={classes.commentButtonWrapper}
          onMouse
        >
          <p className={classes.comment}>
            {tooltip}
          </p>
        </CustomActionCell>
      )}
    </div>
  );

  const onCloseHandler = useCallback(() => {
    setOpen(false);
    if (!touched) {
      // setTimeout(() => setFieldTouched(name as never, true));
    }
  }, [setOpen, touched]); // setFieldTouched

  // trigger validation on blur (touch)
  // const onBlur = useCallback(() => {
  //   setValue(name, fieldValue, { shouldValidate: true });
  // }, [setValue, fieldValue, name]);

  // first init value
  useEffect(() => {
    if (options && options.length > 0 && !initOptions) {
      setInitOptions(true);
      setTextValue(options.find((opt) => opt.id === fieldValue)?.label || '');

      if (options.length === 1 && !initOptions && isRequired) {
        setValue(name, options[0].id, { shouldValidate: true });
        // setInitialOptionsInstalled(true);
        setTextValue(options[0].label);
        if (callback) {
          callback(options[0]);
        }
        // setTimeout(() => setFieldTouched(name as never, true), 5);
      }
    }
  }, [
    setInitOptions,
    options,
    initOptions,
    isRequired,
    fieldValue,
    setValue,
    name,
    callback,
    // setInitialOptionsInstalled,
    emptyValue,
  ]);

  // set default select option if dependent select set empty value
  useEffect(() => {
    if (dependentFieldName) {
      if (dependentFieldValue !== firstDependentValue && dependentDefaultOption) {
        setValue(name, dependentDefaultOption.id, { shouldValidate: true });
        setTextValue(dependentDefaultOption.label);

        if (callback) {
          callback(dependentDefaultOption);
        }
      }
    }
  }, [
    dependentDefaultOption,
    firstDependentValue,
    dependentFieldValue,
    dependentFieldName,
    emptyValue,
    callback,
    setValue,
    name,
  ]);

  // select first item after update options
  useEffect(() => {
    if (selectFirstOption && !disabled && !loading) {
      if (options && options.length) {
        setValue(name, options[0].id, { shouldValidate: true });
        setTextValue(options[0].label);

        if (callback) {
          callback(options[0]);
        }
      } else {
        setValue(name, emptyValue, { shouldValidate: true });
        setTextValue('');

        if (callback) {
          callback();
        }
      }
    }
  }, [options, callback, selectFirstOption, disabled, name, setValue, loading, emptyValue]);

  // Set focus
  useEffect(() => {
    if (updateFocus && !loading && inputRef.current) {
      setUpdateFocus(false);
      inputRef.current.focus();
    }
  }, [options, inputRef, loading, updateFocus, setUpdateFocus]);

  useEffect(() => {
    if (setOption && setOption.id && setOption.label) {
      setValue(name, setOption.id, { shouldValidate: true });
      setTextValue(setOption.label);
    }
  }, [setOption, setValue, setTextValue, name]);

  // save text value before search
  useEffect(() => {
    const option = options.find((opt) => opt.id === fieldValue);

    if (option) {
      setTextValueBeforeSearch(option.label);
    }
  }, [setTextValueBeforeSearch, fieldValue, options]);

  // set previous text value if after search 0 results
  useEffect(() => {
    if (options.length === 0) {
      setTextValue(textValueBeforeSearch);
    }
  }, [textValueBeforeSearch, setTextValue, name, options]);

  return (
    <Autocomplete
      disableClearable={disableClearable || !textValue}
      id={autoCompleteId}
      disabled={disabled || loading}
      open={open && !loading}
      onOpen={() => { setOpen(true); }}
      onClose={onCloseHandler}
      options={options}
      value={textValue}
      getOptionLabel={() => textValue}
      classes={{
        root: classes.root,
        input: classes.input,
        focused: classes.focused,
        popper: classes.popper,
      }}
      onChange={onChange}
      renderOption={(option:Option) => (option.title ? option.title : <>{option.label}</>)}
      renderInput={(params) => (
        <Controller
          name={name}
          render={({ onBlur }) => (
            <>
              <TextField
                onBlur={onBlur}
                inputRef={inputRef}
                {...params}
                value={textValue}
                id={inputId}
                label={labelRender}
                fullWidth
                onChange={onChangeTextField}
                InputLabelProps={{
                  classes: {
                    root: (error && touched && !loading)
                      ? `${classes.labelRoot} ${classes.labelRootError}`
                      : classes.labelRoot,
                    focused: classes.focused,
                  },
                }}
                InputProps={{
                  classes: {
                    input: classes.input,
                    underline: (error && touched && !loading)
                      ? `${classes.inputUnderline} ${classes.inputUnderlineError}`
                      : classes.inputUnderline,
                    focused: classes.focused,
                    disabled: classes.disabled,
                  },
                  ...params.InputProps,
                  startAdornment: loading ? <CircularProgress className={classes.loader} color="secondary" /> : null,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
              {error && !loading && !hideErrors && !disabled && touched && ( // meta.error // touched
                <FormHelperText error id={errorId}>{error.message}</FormHelperText>
              )}
            </>
          )}
        />
      )}
    />
  );
};

export default FormSimpleAutoComplete;
